import { IPrice } from '../interfaces/models/billingPlan'

/**
 * Filters prices to show only active ones for UI display
 * Preserves all prices in the background for existing user subscription validation
 * 
 * @param prices - Array of prices to filter
 * @param interval - 'month' or 'year' to filter by interval
 * @param currency - Currency to filter by (optional)
 * @returns Filtered array of active prices
 */
export const getActivePrices = (prices: IPrice[], interval?: string, currency?: string): IPrice[] => {
    let filteredPrices = prices;

    // Filter by interval if specified
    if (interval) {
        filteredPrices = filteredPrices.filter(price => price.interval === interval);
    }

    // Filter by currency if specified
    if (currency) {
        filteredPrices = filteredPrices.filter(price => price.currency === currency);
    }

    // Group prices by productId to handle multiple prices per product
    const pricesByProductId: { [key: string]: IPrice[] } = {};
    filteredPrices.forEach(price => {
        if (!pricesByProductId[price.productId]) {
            pricesByProductId[price.productId] = [];
        }
        pricesByProductId[price.productId].push(price);
    });

    // For each product, keep only active prices
    const activePrices: IPrice[] = [];
    Object.values(pricesByProductId).forEach(productPrices => {
        if (productPrices.length === 1) {
            // If only one price for this product, keep it (backward compatibility)
            activePrices.push(productPrices[0]);
        } else {
            // If multiple prices, filter by active === true
            const activeProductPrices = productPrices.filter(price => price.active === true);
            if (activeProductPrices.length > 0) {
                activePrices.push(...activeProductPrices);
            } else {
                // Fallback: if no active prices found, keep the first one
                activePrices.push(productPrices[0]);
            }
        }
    });

    return activePrices;
};

/**
 * Gets the active price for a specific interval and currency
 * 
 * @param prices - Array of prices to search
 * @param interval - 'month' or 'year'
 * @param currency - Currency code
 * @returns Active price or undefined if not found
 */
export const getActivePrice = (prices: IPrice[], interval: string, currency: string): IPrice | undefined => {
    const activePrices = getActivePrices(prices, interval, currency);
    return activePrices.length > 0 ? activePrices[0] : undefined;
};

/**
 * Filters billing plans to show only active prices while preserving all prices for background validation
 * 
 * @param billingPlans - Array of billing plans
 * @returns Billing plans with filtered active prices
 */
export const filterBillingPlansForUI = (billingPlans: any[]): any[] => {
    return billingPlans.map(plan => ({
        ...plan,
        prices: plan.prices.map((price: IPrice) => {
            // Keep all prices in the background, but mark inactive ones
            return {
                ...price,
                isActiveForUI: price.active === true || (price.active === undefined && plan.prices.filter((p: IPrice) => p.interval === price.interval && p.currency === price.currency).length === 1)
            };
        })
    }));
};
